import React from 'react';
import ReactMarkdown from 'react-markdown';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ 
  content, 
  className = '' 
}) => {
  // 安全内容验证
  const safeContent = content || '';
  
  // 优化后的 Markdown 检测函数
  const isMarkdown = (text: string): boolean => {
    if (!text || typeof text !== 'string') return false;
    
    // 更全面的 Markdown 模式检测
    const markdownPatterns = [
      /^#{1,6}\s+.+/m,              // 标题: # 标题
      /\*\*[^*\n]+\*\*/,            // 粗体: **文本**
      /\*[^*\n]+\*/,                // 斜体: *文本*
      /^\s*[-*+]\s+.+/m,            // 无序列表: - 项目
      /^\s*\d+\.\s+.+/m,            // 有序列表: 1. 项目
      /```[\s\S]*?```/,             // 代码块: ```代码```
      /`[^`\n]+`/,                  // 行内代码: `代码`
      /\[([^\]]+)\]\(([^)]+)\)/,    // 链接: [文本](链接)
      /^\s*>\s+.+/m,                // 引用: > 引用文本
      /^\s*\|.+\|/m,                // 表格: |列1|列2|
      /---+/,                       // 分割线: ---
      /~~[^~\n]+~~/,                // 删除线: ~~文本~~
    ];
    
    const hasMarkdown = markdownPatterns.some(pattern => {
      try {
        return pattern.test(text);
      } catch (error) {
        console.warn('Markdown pattern test error:', error);
        return false;
      }
    });
    
    // 添加调试日志
    // console.log('Markdown detection:', {
    //   text: text.substring(0, 100) + (text.length > 100 ? '...' : ''),
    //   isMarkdown: hasMarkdown
    // });
    
    return hasMarkdown;
  };

  // 如果不是 Markdown 格式，直接返回普通文本
  if (!isMarkdown(safeContent)) {
    return (
      <div className={className} style={{ color: '#333', lineHeight: '1.5' }}>
        {safeContent}
      </div>
    );
  }

  // 使用 react-markdown 渲染，添加详细的组件配置
  // 移除 remarkGfm 插件以避免 inTable 错误
  return (
    <div className={`markdown-content ${className}`} style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: '1.6',
      color: '#333'
    }}>
      <ReactMarkdown
        components={{
          // 标题组件
          h1: ({children}) => (
            <h1 style={{
              fontSize: '1.5em',
              fontWeight: 'bold',
              marginTop: '1em',
              marginBottom: '0.5em',
              color: '#333',
              borderBottom: '2px solid #eee',
              paddingBottom: '0.3em'
            }}>
              {children}
            </h1>
          ),
          h2: ({children}) => (
            <h2 style={{
              fontSize: '1.3em',
              fontWeight: 'bold',
              marginTop: '0.8em',
              marginBottom: '0.4em',
              color: '#333'
            }}>
              {children}
            </h2>
          ),
          h3: ({children}) => (
            <h3 style={{
              fontSize: '1.1em',
              fontWeight: 'bold',
              marginTop: '0.6em',
              marginBottom: '0.3em',
              color: '#333'
            }}>
              {children}
            </h3>
          ),
          
          // 段落组件
          p: ({children}) => (
            <p style={{
              marginBottom: '0.8em',
              lineHeight: '1.6',
              color: '#333'
            }}>
              {children}
            </p>
          ),
          
          // 粗体组件
          strong: ({children}) => (
            <strong style={{
              fontWeight: 'bold',
              color: '#333'
            }}>
              {children}
            </strong>
          ),
          
          // 斜体组件
          em: ({children}) => (
            <em style={{
              fontStyle: 'italic',
              color: '#333'
            }}>
              {children}
            </em>
          ),
          
          // 有序列表组件
          ol: ({children}) => (
            <ol style={{
              paddingLeft: '1.5em',
              marginBottom: '1em',
              listStyleType: 'decimal'
            }}>
              {children}
            </ol>
          ),
          
          // 无序列表组件
          ul: ({children}) => (
            <ul style={{
              paddingLeft: '1.5em',
              marginBottom: '1em',
              listStyleType: 'disc'
            }}>
              {children}
            </ul>
          ),
          
          // 列表项组件
          li: ({children}) => (
            <li style={{
              marginBottom: '0.3em',
              color: '#333',
              lineHeight: '1.5'
            }}>
              {children}
            </li>
          ),
          
          // 行内代码组件
          code: ({inline, children}) => {
            if (inline) {
              return (
                <code style={{
                  backgroundColor: '#f5f5f5',
                  padding: '0.2em 0.4em',
                  borderRadius: '3px',
                  fontSize: '0.9em',
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  color: '#d73a49',
                  border: '1px solid #e1e4e8'
                }}>
                  {children}
                </code>
              );
            }
            // 对于代码块中的代码，直接返回 children，让 pre 组件处理样式
            return <>{children}</>;
          },
          
          // 代码块组件
          pre: ({children}) => (
            <pre style={{
              backgroundColor: '#f6f8fa',
              padding: '1em',
              borderRadius: '6px',
              fontSize: '0.9em',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              color: '#24292e',
              overflowX: 'auto',
              border: '1px solid #e1e4e8',
              margin: '1em 0'
            }}>
              {children}
            </pre>
          ),
          
          // 链接组件
          a: ({href, children}) => (
            <a 
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#0366d6',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.textDecoration = 'none';
              }}
            >
              {children}
            </a>
          ),
          
          // 引用组件
          blockquote: ({children}) => (
            <blockquote style={{
              borderLeft: '4px solid #dfe2e5',
              paddingLeft: '1em',
              margin: '1em 0',
              color: '#6a737d',
              fontStyle: 'italic',
              backgroundColor: '#f8f9fa'
            }}>
              {children}
            </blockquote>
          ),
          
          // 表格组件
          table: ({children}) => (
            <table style={{
              borderCollapse: 'collapse',
              width: '100%',
              margin: '1em 0',
              border: '1px solid #dfe2e5'
            }}>
              {children}
            </table>
          ),
          
          thead: ({children}) => (
            <thead style={{
              backgroundColor: '#f6f8fa'
            }}>
              {children}
            </thead>
          ),
          
          th: ({children}) => (
            <th style={{
              border: '1px solid #dfe2e5',
              padding: '0.5em',
              fontWeight: 'bold',
              textAlign: 'left'
            }}>
              {children}
            </th>
          ),
          
          td: ({children}) => (
            <td style={{
              border: '1px solid #dfe2e5',
              padding: '0.5em'
            }}>
              {children}
            </td>
          ),
          
          // 分割线组件
          hr: () => (
            <hr style={{
              border: 'none',
              borderTop: '2px solid #eee',
              margin: '2em 0'
            }} />
          ),
          
          // 删除线组件
          del: ({children}) => (
            <del style={{
              textDecoration: 'line-through',
              color: '#6a737d'
            }}>
              {children}
            </del>
          )
        }}
      >
        {safeContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;